import { FC, useEffect, useMemo, useRef } from 'react';
import { useAdyen, useI18n, useLogger, useLoyalty } from '~Hooks';
import { CardIdentity } from '~reducers';
import { DISCOUNT_TYPE } from '~../__queries__';
import { MEMBERSHIP_DISCOUNT_ID } from '~Components/LinkUser/consts';
import { displayName } from '~Components/LoyaltyModal/helpers';
import { useExpressCartActions } from '../ExpressCartProvider';
import {
  ExpressLoyaltyActions,
  ExpressLoyaltyEngineRef,
  ExpressLoyaltyIdentifyResult,
} from './index';

/** How long to wait for an aborted prompt to come back before paying anyway. */
const ABORT_SETTLE_MS = 8000;

/** No card tapped within this long -> the prompt is aborted and the shopper offered a retry. */
const IDENTIFY_TIMEOUT_MS = 60000;

interface Props {
  terminalId: string;
  /** The gate's slot - this engine plugs itself in here while mounted. */
  engineRef: ExpressLoyaltyEngineRef;
}

/**
 * The real loyalty flow, mounted by the gate in index.tsx only when the venue
 * has Loyalty Pro and the device an Adyen terminal. Renders nothing; it
 * registers its actions on the gate's ref. All changing state lives in refs
 * so the actions object is built once.
 */
const ActiveLoyaltyProvider: FC<Props> = ({ terminalId, engineRef }) => {
  const logger = useLogger('epos-loyalty');
  const { i18n } = useI18n();
  const adyen = useAdyen();
  const loyalty = useLoyalty();
  const cartActions = useExpressCartActions();

  const loyaltyRef = useRef(loyalty);
  loyaltyRef.current = loyalty;
  const adyenRef = useRef(adyen);
  adyenRef.current = adyen;
  const i18nRef = useRef(i18n);
  i18nRef.current = i18n;

  /** The acquisition still prompting on the terminal, null when none is. */
  const pendingRef = useRef<Promise<ExpressLoyaltyIdentifyResult> | null>(null);
  /** Its ServiceID - the terminal only lets a prompt be aborted by quoting it. */
  const pendingServiceIdRef = useRef<string | null>(null);
  /** The card the terminal read and still holds - not yet paid with or released. */
  const heldCardRef = useRef<CardIdentity | null>(null);
  /** Bumped by every identify/release - a prompt from an older order must not touch the cart. */
  const generationRef = useRef(0);

  const actions = useMemo<ExpressLoyaltyActions>(() => {
    /** Cancels the prompt and waits (bounded) for the request to come back. */
    const abortPrompt = async () => {
      const pending = pendingRef.current;
      if (!pending) {
        return;
      }
      const serviceId = pendingServiceIdRef.current;
      logger.info('Aborting the loyalty card prompt', { terminalId, serviceId });
      if (serviceId) {
        await adyenRef.current.cancelCardRequest(terminalId, serviceId);
      }
      await Promise.race([
        pending,
        new Promise<void>((resolve) => window.setTimeout(resolve, ABORT_SETTLE_MS)),
      ]);
    };

    const run = async (
      generation: number,
      serviceId: string,
    ): Promise<ExpressLoyaltyIdentifyResult> => {
      const res = await adyenRef.current.requestCard(terminalId, undefined, serviceId);

      // Started over (or a new order began) while the terminal was asking - this prompt is stale.
      if (generation !== generationRef.current) {
        logger.info('Loyalty prompt belongs to an earlier order - ignoring its result', {
          hadCard: res.success,
        });
        if (res.success) {
          await adyenRef.current.cancelCardAcquisition(terminalId);
        }
        return { status: 'cancelled' };
      }

      if (!res.success) {
        // Usually the connection to the terminal giving up after 20-30s while the terminal
        // keeps prompting. Not a deliberate cancel, so the shopper is shown it and can retry.
        logger.warn('Loyalty card prompt ended without a card', { reason: res.message });
        // Stop the prompt still on the terminal, so a retry does not find it busy. Not awaited.
        adyenRef.current.cancelCardRequest(terminalId, serviceId);
        return { status: 'timeout' };
      }

      const card: CardIdentity = {
        par: res.par,
        maskedPan: res.maskedPan,
        brand: res.brand,
        entryMode: res.entryMode,
        reference: res.reference,
      };
      heldCardRef.current = card;

      let memberId: string | null = null;
      let memberName: string | null = null;
      let discount: { value: number; title: string } | null = null;

      try {
        const member = await loyaltyRef.current.identifyByCard(card);
        if (member) {
          memberId = member.id;
          memberName = displayName(member, i18nRef.current);
          const level = member.currentMembershipLevel;
          if (level && level.discountPercentage > 0) {
            discount = {
              value: level.discountPercentage,
              title: `${i18nRef.current('Loyalty.Title')} · ${i18nRef.current(level.name)}`,
            };
          }
        }
      } catch (err) {
        // The card is still good for the payment - only the membership is unknown.
        logger.error('Loyalty lookup failed on the kiosk', {
          reason: err instanceof Error ? err.message : String(err),
        });
      }

      // Released while the lookup ran (start over, idle) - nothing to attach anymore.
      if (heldCardRef.current !== card) {
        logger.info('Card released before the loyalty lookup finished - dropping it');
        return { status: 'cancelled' };
      }

      // Timed out while the lookup ran - the shopper was already told, so hand the card back.
      if (generation !== generationRef.current) {
        logger.info('Loyalty prompt timed out during the lookup - releasing the card', {
          terminalId,
        });
        heldCardRef.current = null;
        await adyenRef.current.cancelCardAcquisition(terminalId);
        return { status: 'cancelled' };
      }

      // Not a member: the shopper is told to try another card, so this one is not kept.
      if (!memberId) {
        logger.info('Card matched no member - releasing it', { terminalId });
        heldCardRef.current = null;
        await adyenRef.current.cancelCardAcquisition(terminalId);
        return { status: 'unknown' };
      }

      logger.info('Loyalty card resolved on the kiosk', {
        memberFound: Boolean(memberId),
        memberId,
        discountPercentage: discount?.value ?? 0,
      });

      cartActions.setLoyalty({
        cardIdentity: memberName ? { ...card, memberName } : card,
        memberId,
        memberName,
      });

      if (discount) {
        cartActions.setAppliedDiscount({
          id: MEMBERSHIP_DISCOUNT_ID,
          type: DISCOUNT_TYPE.PERCENTAGE,
          value: discount.value,
          title: discount.title,
        });
      }
      return { status: 'member', memberName };
    };

    return {
      identify: async () => {
        if (heldCardRef.current) {
          logger.info('Loyalty prompt skipped - a card is already held');
          return { status: 'cancelled' };
        }
        // A prompt from an order that was started over is still on the terminal: cancel it
        // and wait for it to settle, then ask afresh for this order.
        if (pendingRef.current) {
          logger.info('Loyalty prompt still running from an earlier order - restarting it');
          await abortPrompt();
        }
        generationRef.current += 1;
        const generation = generationRef.current;
        /** Same shape the library uses (max 10 digits); handed to requestCard so we can abort it. */
        const serviceId = Math.round(Math.random() * 1000000000).toString();
        logger.info('Prompting the terminal for a loyalty card', {
          terminalId,
          generation,
          serviceId,
        });
        const pending = run(generation, serviceId)
          .catch(
            (err): ExpressLoyaltyIdentifyResult => {
              logger.error('Loyalty card prompt threw', {
                reason: err instanceof Error ? err.message : String(err),
              });
              // Shown like a timeout - closing the modal without a word looks like a crash.
              return { status: 'timeout' };
            },
          )
          .finally(() => {
            // A slow abort can settle after the next prompt started - leave that one alone.
            if (pendingServiceIdRef.current === serviceId) {
              pendingRef.current = null;
              pendingServiceIdRef.current = null;
            }
          });
        pendingRef.current = pending;
        pendingServiceIdRef.current = serviceId;

        let timer = 0;
        const timedOut = new Promise<'timeout'>((resolve) => {
          timer = window.setTimeout(() => resolve('timeout'), IDENTIFY_TIMEOUT_MS);
        });
        const outcome = await Promise.race([pending, timedOut]);
        window.clearTimeout(timer);
        if (outcome !== 'timeout') {
          return outcome;
        }

        logger.warn('Loyalty card prompt timed out - aborting it on the terminal', {
          terminalId,
          generation,
          timeoutMs: IDENTIFY_TIMEOUT_MS,
        });
        // A card that still arrives belongs to a prompt the shopper was told is over.
        generationRef.current += 1;
        // Not awaited: the timeout screen shows now; a retry waits for the abort to settle.
        abortPrompt();
        return { status: 'timeout' };
      },

      release: async () => {
        // Anything the current prompt returns from here on is for an order that no longer exists.
        generationRef.current += 1;
        await abortPrompt();
        if (!heldCardRef.current) {
          return;
        }
        heldCardRef.current = null;
        logger.info('Releasing the unused loyalty card', { terminalId });
        await adyenRef.current.cancelCardAcquisition(terminalId);
      },

      prepareForPayment: async () => {
        await abortPrompt();
        // The payment takes ownership of the card from here on.
        const card = heldCardRef.current;
        heldCardRef.current = null;
        logger.info('Loyalty card handed to the payment', { hasCard: Boolean(card) });
        return card;
      },
    };
  }, [terminalId, cartActions]);

  useEffect(() => {
    engineRef.current = actions;
    return () => {
      engineRef.current = null;
    };
  }, [actions, engineRef]);

  return null;
};

export default ActiveLoyaltyProvider;
